import React, { useRef, useState } from "react";
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Icon,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Button,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { HiOutlineEllipsisHorizontal, HiOutlineArrowTopRightOnSquare, HiOutlineTrash } from "react-icons/hi2";
import { c, PALETTES } from "./classroomColors";

/**
 * The "..." in a classroom card's corner (Figma 114:50): open the classroom,
 * or delete it behind a confirmation. Menus and dialogs render in a portal
 * outside the theme wrapper, so they take literal palette values.
 */
const ClassroomMenu = ({ classroomName, isDark = true, onOpen, onDelete }) => {
  const p = isDark ? PALETTES.dark : PALETTES.light;
  const confirm = useDisclosure();
  const cancelRef = useRef(null);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onDelete();
      confirm.onClose();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Menu placement="bottom-end" autoSelect={false}>
        <MenuButton
          as={IconButton}
          aria-label={`Options for ${classroomName}`}
          icon={<Icon as={HiOutlineEllipsisHorizontal} boxSize="20px" />}
          variant="ghost"
          size="sm"
          minW="28px"
          h="28px"
          borderRadius="8px"
          color={c.textSub}
          _hover={{ bg: c.cardPill, color: c.text }}
          _active={{ bg: c.cardPill, color: c.text }}
        />
        <MenuList
          minW="190px"
          py="6px"
          bg={p.surface}
          color={p.text}
          border="1px solid"
          borderColor={p.border}
          borderRadius="10px"
          boxShadow="0 12px 30px -12px rgba(0,0,0,0.4)"
          fontSize="13px"
        >
          <MenuItem
            icon={<Icon as={HiOutlineArrowTopRightOnSquare} boxSize="16px" />}
            bg="transparent"
            _hover={{ bg: p.hover }}
            _focus={{ bg: p.hover }}
            onClick={onOpen}
          >
            Open space
          </MenuItem>
          <MenuItem
            icon={<Icon as={HiOutlineTrash} boxSize="16px" />}
            bg="transparent"
            color={isDark ? "#ff8a7a" : "#d93025"}
            _hover={{ bg: p.hover }}
            _focus={{ bg: p.hover }}
            onClick={confirm.onOpen}
          >
            Delete space
          </MenuItem>
        </MenuList>
      </Menu>

      <AlertDialog isOpen={confirm.isOpen} leastDestructiveRef={cancelRef} onClose={confirm.onClose} isCentered>
        <AlertDialogOverlay bg={p.overlay}>
          <AlertDialogContent bg={p.surface} color={p.text} borderRadius="14px" border="1px solid" borderColor={p.border} maxW="420px">
            <AlertDialogHeader fontSize="18px" fontWeight="500" pb="6px">
              Delete {classroomName}?
            </AlertDialogHeader>
            <AlertDialogBody pt="0">
              <Text fontSize="14px" color={p.textSub} fontWeight="300" lineHeight="1.5">
                The space is removed from your list and your students' lists. Their
                submitted work and feedback are kept.
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter gap="8px" pt="16px">
              <Button
                ref={cancelRef}
                onClick={confirm.onClose}
                variant="ghost"
                size="sm"
                borderRadius="8px"
                color={p.text}
                _hover={{ bg: p.hover }}
              >
                Keep it
              </Button>
              <Button
                onClick={handleDelete}
                isLoading={deleting}
                loadingText="Deleting"
                size="sm"
                borderRadius="8px"
                bg={isDark ? "#ff8a7a" : "#d93025"}
                color={isDark ? "#222222" : "#ffffff"}
                _hover={{ opacity: 0.9 }}
              >
                Delete space
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default ClassroomMenu;
